import FlagIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Flag.js';
import { LensTheme, StatusIndicator } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Avatar,
  AvatarGroup,
  Box,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
} from '@mui/material';
import React from 'react';
import {
  DisclosureDataset,
  RowModel,
} from 'src/components/pages/CoiDisclosures/types';
import { getReadableIdWithDash } from 'src/types';
import { capitaliseFirstLetter } from 'src/utils';
import { getInitials } from 'src/utils/getInitials';
import { CoiStatusColorMap } from '../helpers/CoiStatusColorMap';
import * as styles from './DisclosuresTable.styles';

export interface DisclosuresTableProps {
  rows: RowModel[];
  dataset: NonNullable<DisclosureDataset>;
  isLoading: boolean;
  isError: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRowClick: (id: string) => void;
  emptyState: React.ReactNode;
}

const REVIEWER_GROUP_MAX = 4;

const BASE_COLUMNS = [
  'Status',
  'Disclosure ID',
  'Form title',
  'Category name',
  'Employee name',
  'Country',
  'Submission date',
  'Flag count',
];

const COLUMNS_BY_DATASET: Record<NonNullable<DisclosureDataset>, string[]> = {
  open: [...BASE_COLUMNS, 'Reviewer', 'Reviewer group'],
  closed: [...BASE_COLUMNS, 'Reviewer', 'Reviewer group'],
  'auto-closed': BASE_COLUMNS,
};

export const DisclosuresTable: React.FC<DisclosuresTableProps> = ({
  rows,
  dataset,
  isLoading,
  isError,
  page,
  totalPages,
  onPageChange,
  onRowClick,
  emptyState,
}) => {
  const { presets } = useTheme<LensTheme>();
  const { getAvatarProps } = presets.AvatarPresets;
  const columns = COLUMNS_BY_DATASET[dataset ?? 'open'];
  const showReviewerColumns = dataset !== 'auto-closed';

  if (isLoading) {
    return (
      <Box data-testid="disclosures-table-loading" sx={styles.loading}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !rows.length) {
    return <Box sx={styles.emptyWrapper}>{emptyState}</Box>;
  }

  return (
    <Box>
      <TableContainer sx={styles.tableContainer}>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow sx={styles.headerRow}>
              {columns.map((col) => (
                <TableCell key={col} sx={styles.headerCell}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const initials =
                row.reviewer === '-' ? undefined : getInitials(row.reviewer);
              const normalizedStatus = row.status.replaceAll('_', ' ');
              return (
                <TableRow
                  key={row.id}
                  hover
                  data-testid={`disclosures-row-${row.id}`}
                  onClick={() => onRowClick(row.id)}
                  sx={styles.row}
                >
                  <TableCell sx={styles.cell}>
                    <StatusIndicator
                      label={capitaliseFirstLetter(normalizedStatus)}
                      customColor={CoiStatusColorMap(normalizedStatus)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={styles.cell}>
                    {getReadableIdWithDash(row.id).toUpperCase()}
                  </TableCell>
                  <TableCell sx={styles.cell}>
                    <Box component="span" sx={styles.formTitle}>
                      {row.formTitle}
                    </Box>
                  </TableCell>
                  <TableCell sx={styles.cell}>{row.category}</TableCell>
                  <TableCell sx={styles.cell}>{row.employee}</TableCell>
                  <TableCell sx={styles.cell}>{row.country}</TableCell>
                  <TableCell sx={styles.cell}>{row.submittedDate}</TableCell>
                  <TableCell sx={styles.cell}>
                    <Box sx={styles.flagCell}>
                      <FlagIcon aria-hidden="true" />
                      {row.flags}
                    </Box>
                  </TableCell>
                  {showReviewerColumns && (
                    <>
                      <TableCell sx={styles.cell}>
                        {initials ? (
                          <Box sx={styles.reviewerCell}>
                            <Avatar
                              {...getAvatarProps({
                                size: 'medium',
                                color: 'red',
                              })}
                              aria-label={row.reviewer}
                              role="img"
                            >
                              {initials}
                            </Avatar>
                            {row.reviewer}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell sx={styles.cell}>
                        {row.reviewerGroup.length ? (
                          <AvatarGroup
                            aria-label="Reviewer group"
                            max={REVIEWER_GROUP_MAX}
                            sx={styles.reviewerGroupCell}
                            slotProps={{
                              surplus: {
                                sx: {
                                  ...getAvatarProps({ size: 'medium' }).sx,
                                },
                              },
                            }}
                            renderSurplus={(surplus) => (
                              <Tooltip
                                title={row.reviewerGroup
                                  .slice(REVIEWER_GROUP_MAX - 1)
                                  .join(', ')}
                              >
                                <span>+{surplus}</span>
                              </Tooltip>
                            )}
                          >
                            {row.reviewerGroup.map((member, index) => (
                              <Tooltip
                                key={`${member}-${index}`}
                                title={member}
                              >
                                <Avatar
                                  {...getAvatarProps({ size: 'medium' })}
                                  aria-label={member}
                                  role="img"
                                >
                                  {getInitials(member)}
                                </Avatar>
                              </Tooltip>
                            ))}
                          </AvatarGroup>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={styles.paginationRow}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => onPageChange(value)}
        />
      </Box>
    </Box>
  );
};
